import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { EventInfoDialog } from "@/features/study-group-events/components/EventInfoDialog";
import { ReminderEmailEditor } from "@/features/study-group-events/components/ReminderEmailEditor";
import { SendNowDialog } from "@/features/study-group-events/components/SendNowDialog";
import { buildEventInfoItems } from "@/features/study-group-events/utils/eventInfo";
import { useEntryEmailTemplate } from "@/hooks/useEntryEmailTemplate";
import { useReminderEmailForm } from "@/hooks/useReminderEmailForm";
import { toast } from "@/hooks/useToast";
import { useUpdateEventEmail } from "@/hooks/useUpdateEventEmail";
import type { EventApiResponse, UserShort } from "@/lib/humhub-api/types";
import { queryInvalidationHelpers } from "@/lib/queryInvalidationHelpers";
import { formatEventListRow } from "@/lib/utils/v2/event-date";

interface SendEmailNowDialogProps {
  open: boolean;
  event: EventApiResponse;
  participants: UserShort[];
  primaryImageGuid?: string | null;
  onOpenChange: (open: boolean) => void;
  onDirtyChange: (dirty: boolean) => void;
  hasEmailUnsavedChanges: boolean;
  onShowUnsavedWarning: () => void;
}

export const SendEmailNowDialog = ({
  open,
  event,
  participants,
  primaryImageGuid,
  onOpenChange,
  onDirtyChange,
  hasEmailUnsavedChanges,
  onShowUnsavedWarning,
}: SendEmailNowDialogProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [isSendNowDialogOpen, setIsSendNowDialogOpen] = useState(false);
  const [isSendNowSubmitting, setIsSendNowSubmitting] = useState(false);
  const [isEventInfoDialogOpen, setIsEventInfoDialogOpen] = useState(false);
  const [sendNowHandler, setSendNowHandler] = useState<
    (() => Promise<void> | void) | null
  >(null);

  const subjectFieldId = useId();
  const bodyFieldId = useId();
  const hasFormChangesRef = useRef(false);
  const subjectInputRef = useRef<HTMLInputElement>(null);
  const bodyTextareaRef = useRef<HTMLDivElement>(null);

  const eventDatetimeLabel = useMemo(() => {
    if (!event?.start_datetime)
      return t("common.dateNotAvailable", "Date not available");
    const baseLabel = formatEventListRow(event, {
      year: "auto",
      displayTimezone: "event",
    });
    const tzLabel = event.time_zone_str?.trim();
    return tzLabel ? `${baseLabel} (${tzLabel})` : baseLabel;
  }, [event, t]);

  const eventInfoItems = useMemo(
    () =>
      buildEventInfoItems({
        title: event.title,
        description: event.description,
        datetimeLabel: eventDatetimeLabel,
        url: event.online_location,
        offlineLocation: event.location,
        translate: t,
      }),
    [
      event.title,
      event.description,
      eventDatetimeLabel,
      event.online_location,
      event.location,
      t,
    ],
  );

  const { mutateAsync: updateEventEmail } = useUpdateEventEmail();

  // Fetch entry email template
  const dirtyRef = useRef(false);
  const {
    subject: fetchedEntrySubject,
    bodyText: fetchedEntryBodyText,
    bodyHtml: fetchedEntryBodyHtml,
  } = useEntryEmailTemplate({
    eventId: event.id,
    enabled: open,
    dirtyRef,
  });

  const [entryEmailSubject, setEntryEmailSubject] = useState<string>("");
  const [entryEmailBodyText, setEntryEmailBodyText] = useState<string>("");
  const [entryEmailBodyHtml, setEntryEmailBodyHtml] = useState<string | null>(
    null,
  );

  useEffect(() => {
    if (hasEmailUnsavedChanges) return;
    setEntryEmailSubject(fetchedEntrySubject);
    setEntryEmailBodyText(fetchedEntryBodyText);
    setEntryEmailBodyHtml(fetchedEntryBodyHtml);
  }, [
    fetchedEntryBodyHtml,
    fetchedEntryBodyText,
    fetchedEntrySubject,
    hasEmailUnsavedChanges,
  ]);

  // Manual send email form
  const {
    subject: manualSubject,
    preview: manualPreview,
    placeholders: manualPlaceholders,
    isSubmitting: isManualSubmitting,
    hasChanges: hasManualChanges,
    handleSubjectChange: handleManualSubjectChange,
    handleBodyChange: handleManualBodyChange,
    handleSendNow: handleManualSendNow,
    handleSendTest: handleManualSendTest,
    handleInsertImagePlaceholder: handleManualInsertImagePlaceholder,
    handleInsertTimeUntilEventPlaceholder:
      handleManualInsertTimeUntilEventPlaceholder,
    buildEmailPayload: buildManualEmailPayload,
    markAsSaved: markManualAsSaved,
  } = useReminderEmailForm({
    eventId: event.id,
    eventTitle: event.title,
    reminderId: null,
    scope: "entry",
    defaultSubject: entryEmailSubject,
    defaultBodyText: entryEmailBodyText,
    defaultBodyHtml: entryEmailBodyHtml,
    primaryImageGuid,
  });

  useEffect(() => {
    hasFormChangesRef.current = hasManualChanges;
    onDirtyChange(hasManualChanges);
  }, [hasManualChanges, onDirtyChange]);

  const handleSaveManualTemplate = async () => {
    const { validationError, payload } = buildManualEmailPayload();
    if (validationError) {
      toast({
        title: t("common.error"),
        description: validationError,
        variant: "error",
      });
      return;
    }

    try {
      await updateEventEmail({
        eventId: event.id,
        email_subject: payload.subject,
        email_body_html: payload.bodyHtml,
        email_body_text: payload.bodyText,
        save_for_reminder: false,
        save_for_entry: true,
        send_now: false,
        test_mode: false,
      });

      markManualAsSaved();
      hasFormChangesRef.current = false;

      toast({
        title: t("common.success"),
        description: t("eventManage.reminderEmailUpdated"),
      });
    } catch (error) {
      console.error("Failed to save manual template:", error);
      toast({
        title: t("common.error"),
        description: t("eventManage.reminderUpdateFailed"),
        variant: "error",
      });
    }
  };

  const openSendNowDialog = (handler: () => Promise<void> | void) => {
    setSendNowHandler(() => handler);
    setIsSendNowDialogOpen(true);
  };

  const confirmSendNow = async () => {
    if (!sendNowHandler) {
      setIsSendNowDialogOpen(false);
      return;
    }
    setIsSendNowSubmitting(true);
    setIsSendNowDialogOpen(false);
    try {
      await sendNowHandler();
      queryInvalidationHelpers.invalidateEventQueries(queryClient);
      queryClient.invalidateQueries({ queryKey: ["eventDetail", event.id] });
    } finally {
      setSendNowHandler(null);
      setIsSendNowSubmitting(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(nextOpen) => {
          if (!nextOpen && hasEmailUnsavedChanges) {
            onShowUnsavedWarning();
          } else {
            onOpenChange(nextOpen);
          }
        }}
      >
        <DialogContent className="max-h-[calc(100vh-2rem)] w-[calc(100%-2rem)] max-w-4xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t("eventManage.sendNotificationNow")}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <ReminderEmailEditor
              subjectFieldId={subjectFieldId}
              bodyFieldId={bodyFieldId}
              subject={manualSubject}
              body={manualPreview.bodyHtml}
              isSubmitting={isManualSubmitting}
              hasChanges={hasManualChanges}
              showImageButton={Boolean(primaryImageGuid)}
              onSubjectChange={handleManualSubjectChange}
              onBodyChange={handleManualBodyChange}
              imagePlaceholderHtml={manualPlaceholders.imageHtml}
              timeUntilEventPlaceholderHtml={
                manualPlaceholders.timeUntilEventHtml
              }
              onOpenEventInfo={() => setIsEventInfoDialogOpen(true)}
              onOpenSendNowDialog={() => openSendNowDialog(handleManualSendNow)}
              onSendTest={handleManualSendTest}
              onSave={handleSaveManualTemplate}
              onInsertImagePlaceholder={handleManualInsertImagePlaceholder}
              onInsertTimeUntilEventPlaceholder={
                handleManualInsertTimeUntilEventPlaceholder
              }
              showTimeUntilButton={false}
              showSendNowButton
              disableSendNow={event.is_draft === 1}
              sendNowDisabledReason={
                event.is_draft === 1
                  ? t("eventManage.reminderEmailSendNowDisabledDraft")
                  : null
              }
              subjectInputRef={subjectInputRef}
              bodyTextareaRef={bodyTextareaRef}
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialogs */}
      <SendNowDialog
        open={isSendNowDialogOpen}
        participants={participants}
        isSubmitting={isSendNowSubmitting || isManualSubmitting}
        onOpenChange={setIsSendNowDialogOpen}
        onConfirm={confirmSendNow}
      />

      <EventInfoDialog
        open={isEventInfoDialogOpen}
        onOpenChange={setIsEventInfoDialogOpen}
        items={eventInfoItems}
        onCopy={async (value, label) => {
          if (value) {
            await navigator.clipboard.writeText(value);
            toast({
              title: t("common.success"),
              description: t("common.copiedToClipboard", { label }),
            });
          }
        }}
      />
    </>
  );
};
